/**
 * Étape 4 — le suivi, une ligne par assureur (chunk 9.AZ.1).
 *
 * Chaque assureur a reçu **son** courriel. Son statut lui appartient donc : un
 * rebond chez l'un ne dit rien des autres, et une réponse de l'un n'avance pas
 * les autres. L'écran rend cette indépendance évidente — c'est le bénéfice
 * concret d'avoir refusé la copie conforme invisible.
 */

import type { QuoteRequestRecipientRow } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

import { useInsurerCatalog } from "../../../lib/queries/insurers";
import { InsurerEmailBlockNotice } from "../../projects/_parts/InsurerEmailBlockNotice";
import { RecipientStatusBadge } from "../../projects/_parts/RecipientStatusBadge";

import { Etape } from "./Etape";

export function SuiviParAssureur(props: {
  destinataires: readonly QuoteRequestRecipientRow[];
  /**
   * Le rang de l'étape dans le parcours.
   *
   * Paramétré depuis 9.BK.3 : l'étape de l'effectif s'intercale quand le
   * dossier en porte un et **disparaît** sinon. Un numéro codé en dur
   * produirait une suite trouée — 1, 3, 4 — qui ferait chercher l'étape
   * absente. La page sait quelles étapes elle rend ; elle les numérote.
   */
  numero?: number;
}) {
  const { t, i18n } = useTranslation();
  const insurers = useInsurerCatalog();

  return (
    <Etape
      statusLabel={t("flow.history")}
      numero={props.numero ?? 4}
      titre={t("prepare_send.tracking.title")}
      description={t("prepare_send.tracking.subtitle")}
      etat={props.destinataires.some((d) => d.email_block_reason) ? "stale" : "ready"}
    >
      {props.destinataires.length === 0 ? (
        <p className="text-sm text-slate-600">{t("prepare_send.tracking.empty")}</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {props.destinataires.map((d) => (
            <li key={d.id} className="flex flex-col gap-3 px-4 py-3">
              <span className="w-full min-w-0">
                <span className="block break-words text-sm font-medium text-slate-900">
                  {insurers.labelFor(d.insurer_code)}
                </span>
                <span className="block break-words text-xs text-slate-500 [overflow-wrap:anywhere]">
                  {d.recipient_email ?? "—"}
                </span>
              </span>
              <div className="flex flex-wrap items-center gap-3">
                <span className="shrink-0 text-xs tabular-nums text-slate-500">
                  {d.sent_at ? new Date(d.sent_at).toLocaleDateString(i18n.language) : ""}
                </span>
                <InsurerEmailBlockNotice
                  reason={d.email_block_reason}
                  feedback={d.delivery_feedback}
                />
                <RecipientStatusBadge status={d.status} deliveryFeedback={d.delivery_feedback} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </Etape>
  );
}
